/**
 * useLiveActivity — visitor behaviour capture hook.
 *
 * Mounted from ChatbotWidget once the visitor has bootstrapped successfully
 * (we reuse the same visitor JWT + visitorId; one identity per browser).
 *
 * Captures throttled DOM events, buffers locally, flushes a batch every 2s
 * to POST /api/storefront/:slug/visitor-events. Auto-disconnects after 30s
 * of silence (no listeners detached — capture functions just no-op on empty
 * queue, and the next event reactivates the flush loop).
 *
 * Privacy guardrails enforced client-side:
 *   - Honors `navigator.doNotTrack === '1'` → never starts
 *   - Skips any element marked with `data-private` (or inside one)
 *   - Never captures form input *values*, only field names on focus
 *   - Caps target text at 200 chars (server re-caps + strips PII anyway)
 */

import { useEffect, useRef } from 'react';
import axios from 'axios';
import axiosInstance from '../../../axiosConfig';

// Bare axios — no merchant cookies / interceptors. Matches ChatbotWidget.
const visitorHttp = axios.create({
  baseURL: axiosInstance?.defaults?.baseURL || '/api',
  withCredentials: false,
  timeout: 15000,
});

const FLUSH_INTERVAL_MS = 2000;
const IDLE_DISCONNECT_MS = 30000;
const MAX_QUEUE = 50;

// Click throttle: cap to 6 events/sec
const CLICK_THROTTLE_WINDOW_MS = 1000;
const CLICK_THROTTLE_MAX = 6;

// Hover requires this much dwell time on a single element to fire
const HOVER_DWELL_MS = 800;

function generateSessionId() {
  const bytes = new Uint8Array(8);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(bytes);
  else for (let i = 0; i < 8; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function isPrivate(el) {
  if (!el || el === document) return false;
  // Walk up looking for an explicit opt-out
  let cur = el;
  for (let i = 0; cur && i < 6; i += 1) {
    if (cur.dataset && (cur.dataset.private === '' || cur.dataset.private === 'true')) return true;
    if (cur.tagName === 'INPUT' || cur.tagName === 'TEXTAREA') {
      // Never capture inside form fields
      return true;
    }
    cur = cur.parentElement;
  }
  return false;
}

function describeTarget(el) {
  if (!el || !el.tagName) return { selector: null, text: null };
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : '';
  const cls = el.className && typeof el.className === 'string'
    ? `.${el.className.trim().split(/\s+/).slice(0, 2).join('.')}` : '';
  const selector = `${tag}${id}${cls}`.slice(0, 120);
  // Text content of buttons / links / headings is useful context
  let text = null;
  if (['button', 'a', 'h1', 'h2', 'h3', 'h4', 'h5'].includes(tag)) {
    text = (el.textContent || '').trim().slice(0, 200) || null;
  }
  return { selector, text };
}

export default function useLiveActivity({ visitorId, token, storeSlug, enabled }) {
  const sessionIdRef = useRef(null);
  const queueRef = useRef([]);
  const lastFlushAtRef = useRef(Date.now());
  const flushTimerRef = useRef(null);
  const idleTimerRef = useRef(null);
  const clickWindowRef = useRef({ startedAt: 0, count: 0 });
  const hoverElRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const seenHoverRef = useRef(new WeakSet());
  const scrollMarksRef = useRef(new Set());

  useEffect(() => {
    if (!enabled || !visitorId || !token || !storeSlug) return undefined;
    if (typeof navigator !== 'undefined' && navigator.doNotTrack === '1') {
      return undefined;
    }

    if (!sessionIdRef.current) sessionIdRef.current = generateSessionId();

    // -------------- enqueue + flush -------------------------------------

    const enqueue = (ev) => {
      if (!ev) return;
      const queue = queueRef.current;
      if (queue.length >= MAX_QUEUE) return;
      queue.push({ ...ev, capturedAt: Date.now() });
      scheduleIdleDisconnect();
    };

    const flush = async () => {
      const queue = queueRef.current;
      if (queue.length === 0) return;
      const batch = queue.splice(0, MAX_QUEUE);
      lastFlushAtRef.current = Date.now();
      try {
        await visitorHttp.post(
          `/storefront/${storeSlug}/visitor-events`,
          { sessionId: sessionIdRef.current, events: batch },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        // Drop on failure — we never retry; behaviour data isn't worth
        // retry-storming the server during outages.
      }
    };

    flushTimerRef.current = setInterval(flush, FLUSH_INTERVAL_MS);

    const scheduleIdleDisconnect = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        // Flush whatever we have and back off until the next event
        flush();
      }, IDLE_DISCONNECT_MS);
    };

    // -------------- capture handlers ------------------------------------

    let lastPath = window.location.pathname + window.location.search;
    enqueue({ eventType: 'page_view', pagePath: lastPath, pageTitle: document.title });

    const onPopState = () => {
      const newPath = window.location.pathname + window.location.search;
      if (newPath !== lastPath) {
        lastPath = newPath;
        scrollMarksRef.current.clear();
        enqueue({ eventType: 'navigated', pagePath: newPath, pageTitle: document.title });
        enqueue({ eventType: 'page_view', pagePath: newPath, pageTitle: document.title });
      }
    };

    const onClick = (e) => {
      const el = e.target;
      if (!el || isPrivate(el)) return;

      // Throttle to N clicks/sec
      const now = Date.now();
      const window_ = clickWindowRef.current;
      if (now - window_.startedAt > CLICK_THROTTLE_WINDOW_MS) {
        window_.startedAt = now;
        window_.count = 0;
      }
      window_.count += 1;
      if (window_.count > CLICK_THROTTLE_MAX) return;

      const { selector, text } = describeTarget(el);
      enqueue({
        eventType: 'click',
        pagePath: window.location.pathname + window.location.search,
        target: selector,
        targetText: text,
      });
    };

    const onScroll = () => {
      const doc = document.documentElement;
      if (!doc) return;
      const total = (doc.scrollHeight - doc.clientHeight) || 1;
      const pct = Math.round(((doc.scrollTop || window.scrollY || 0) / total) * 100);
      for (const mark of [25, 50, 75, 100]) {
        if (pct >= mark && !scrollMarksRef.current.has(mark)) {
          scrollMarksRef.current.add(mark);
          enqueue({
            eventType: 'scroll',
            pagePath: window.location.pathname + window.location.search,
            metadata: { depthPercent: mark },
          });
        }
      }
    };

    const onVisibility = () => {
      enqueue({
        eventType: document.hidden ? 'tab_hidden' : 'tab_visible',
        pagePath: window.location.pathname + window.location.search,
      });
    };

    const onMouseOver = (e) => {
      const el = e.target;
      if (!el || isPrivate(el)) return;
      // Only consider product/category tiles or links
      const card = el.closest('[data-product-card], [data-category-card], a[href]');
      if (!card || seenHoverRef.current.has(card)) return;
      hoverElRef.current = card;
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = setTimeout(() => {
        if (hoverElRef.current !== card) return;
        if (seenHoverRef.current.has(card)) return;
        seenHoverRef.current.add(card);
        const { selector, text } = describeTarget(card);
        enqueue({
          eventType: 'hover_significant',
          pagePath: window.location.pathname + window.location.search,
          target: selector,
          targetText: text,
        });
      }, HOVER_DWELL_MS);
    };
    const onMouseOut = () => {
      hoverElRef.current = null;
      if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null; }
    };

    const onFocus = (e) => {
      const el = e.target;
      if (!el || !(el instanceof HTMLElement)) return;
      if (!['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return;
      // Capture field NAME only (never the value)
      const name = el.getAttribute('name') || el.getAttribute('aria-label') || el.id;
      if (!name) return;
      enqueue({
        eventType: 'form_focus',
        pagePath: window.location.pathname + window.location.search,
        target: String(name).slice(0, 80),
      });
    };

    // Detect storefront-specific signals (product / category page)
    if (lastPath.includes('/product/')) {
      enqueue({ eventType: 'viewing_product', pagePath: lastPath, pageTitle: document.title });
    } else if (lastPath.includes('/category/')) {
      enqueue({ eventType: 'viewing_category', pagePath: lastPath, pageTitle: document.title });
    }

    window.addEventListener('popstate', onPopState);
    document.addEventListener('click', onClick, { capture: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('mouseover', onMouseOver);
    document.addEventListener('mouseout', onMouseOut);
    document.addEventListener('focusin', onFocus);

    // -------------- cleanup ---------------------------------------------

    return () => {
      window.removeEventListener('popstate', onPopState);
      document.removeEventListener('click', onClick, { capture: true });
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('mouseover', onMouseOver);
      document.removeEventListener('mouseout', onMouseOut);
      document.removeEventListener('focusin', onFocus);
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      // Final flush attempt on unmount
      flush();
    };
  }, [enabled, visitorId, token, storeSlug]);
}
